/*
 * Set builder — a "set" PDP made of GROUPS. Each group is filled with one
 * product; a group may offer several products to choose from (e.g. "Choose your
 * tee"), and each chosen product has its own colour + size.
 *
 * Pricing: the SET is a normal product with its own price (discounted) and
 * compare-at (full) — shown natively by Liquid, no discount app needed. On add,
 * we add the SET product's own variant with the customer's choices recorded as
 * line-item properties (for fulfilment).
 */
class SetBuilder extends HTMLElement {
  connectedCallback() {
    this.setHandle = this.dataset.setHandle;
    this.setTitle = this.dataset.setTitle;
    this.setVariantId = Number(this.dataset.setVariantId);

    this.addBtn = this.querySelector('[data-set-add]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.noteEl = this.querySelector('[data-set-note]');

    this.groups = [...this.querySelectorAll('[data-set-group]')].map((el) => this.initGroup(el));

    this.addBtn?.addEventListener('click', () => this.addToCart());
    this.updateSummary();
  }

  initGroup(el) {
    const gi = el.dataset.setGroup;
    const multi = el.dataset.multi === 'true';
    const labelEl = el.querySelector('.set-group__label');
    const state = {
      el,
      gi,
      multi,
      label: labelEl ? labelEl.textContent.trim() : `Piece ${gi}`,
      product: null,
      color: null,
      size: null,
      variantsByProduct: {},
      preview: this.querySelector(`[data-preview-img="${gi}"]`),
    };

    el.querySelectorAll('[data-set-product]').forEach((pEl) => {
      const h = pEl.dataset.setProduct;
      let vars = [];
      try {
        vars = JSON.parse(el.querySelector(`[data-variants="${gi}-${h}"]`).textContent);
      } catch (e) {
        vars = [];
      }
      state.variantsByProduct[h] = vars;
      pEl.querySelectorAll('.set-swatch').forEach((btn) =>
        btn.addEventListener('click', () => this.pickColor(state, pEl, btn))
      );
      pEl.querySelectorAll('.set-size').forEach((btn) =>
        btn.addEventListener('click', () => this.pickSize(state, pEl, btn))
      );
    });

    if (multi) {
      el.querySelectorAll('.set-choice').forEach((btn) =>
        btn.addEventListener('click', () => this.pickProduct(state, el, btn))
      );
    } else {
      const only = el.querySelector('[data-set-product]');
      if (only) {
        state.product = only.dataset.setProduct;
        only.hidden = false;
      }
    }

    return state;
  }

  pickProduct(state, el, btn) {
    state.product = btn.dataset.product;
    state.color = null;
    state.size = null;
    el.querySelectorAll('.set-choice').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    el.querySelectorAll('[data-set-product]').forEach((pEl) => {
      pEl.hidden = pEl.dataset.setProduct !== state.product;
      // reset any prior selections in the now-hidden product
      pEl.querySelectorAll('[aria-pressed="true"]').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      const lbl = pEl.querySelector('[data-selected-color]');
      if (lbl) lbl.innerHTML = '&mdash;';
      pEl.querySelectorAll('.set-size').forEach((s) => (s.disabled = false));
    });
    if (btn.dataset.image && state.preview) state.preview.src = btn.dataset.image;
    this.updateSummary();
  }

  pickColor(state, pEl, btn) {
    if (pEl.dataset.setProduct !== state.product) return;
    state.color = btn.dataset.color;
    pEl.querySelectorAll('.set-swatch').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    const lbl = pEl.querySelector('[data-selected-color]');
    if (lbl) lbl.textContent = state.color;
    if (btn.dataset.image && state.preview) state.preview.src = btn.dataset.image;
    this.refreshSizes(state, pEl);
    this.updateSummary();
  }

  pickSize(state, pEl, btn) {
    if (btn.disabled || pEl.dataset.setProduct !== state.product) return;
    state.size = btn.dataset.size;
    pEl.querySelectorAll('.set-size').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    this.updateSummary();
  }

  refreshSizes(state, pEl) {
    const vars = state.variantsByProduct[state.product] || [];
    pEl.querySelectorAll('.set-size').forEach((btn) => {
      const m = vars.find((v) => v.color === state.color && v.size === btn.dataset.size);
      const ok = m && m.available;
      btn.disabled = !ok;
      if (!ok && state.size === btn.dataset.size) {
        state.size = null;
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  resolve(state) {
    if (!state.product || !state.color || !state.size) return null;
    const vars = state.variantsByProduct[state.product] || [];
    return vars.find((v) => v.color === state.color && v.size === state.size) || null;
  }

  updateSummary() {
    const resolved = this.groups.map((g) => this.resolve(g));
    const done = resolved.filter(Boolean).length;
    const total = this.groups.length;

    if (done === total) {
      this.addBtn.disabled = false;
      this.addLabel.textContent = 'Add set to cart';
      this.setNote('');
    } else {
      this.addBtn.disabled = true;
      this.addLabel.textContent = `Select options (${done}/${total})`;
    }
  }

  setNote(msg, isError) {
    if (!this.noteEl) return;
    this.noteEl.textContent = msg || '';
    this.noteEl.hidden = !msg;
    this.noteEl.style.color = isError ? 'var(--color-error, #c0392b)' : '';
  }

  // Human-readable name of the product currently active in a group.
  productName(state) {
    const pEl =
      state.el.querySelector('[data-set-product]:not([hidden])') ||
      state.el.querySelector('[data-set-product]');
    const nameEl = pEl && pEl.querySelector('.set-product__name');
    return nameEl ? nameEl.textContent.trim() : state.product;
  }

  async addToCart() {
    const resolved = this.groups.map((g) => this.resolve(g));
    if (resolved.some((v) => !v)) return;

    this.addBtn.disabled = true;
    this.addLabel.textContent = 'Adding…';

    // Record each group's choice as a line-item property on the SET product,
    // e.g. { "Top": "Riviera Linen Shirt — Aqua Blue / M/L" }.
    const properties = {};
    this.groups.forEach((g) => {
      properties[g.label] = `${this.productName(g)} — ${g.color} / ${g.size}`;
    });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.setVariantId, quantity: 1, properties }] }),
      });
      if (!res.ok) throw new Error('add failed');
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const drawer = document.querySelector('cart-drawer, #cart-drawer, [ref="cartDrawer"]');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        this.addLabel.textContent = 'Added ✓';
        this.addBtn.disabled = false;
      } else {
        window.location.href = '/cart';
      }
    } catch (e) {
      this.setNote('Sorry — could not add the set. Please try again.', true);
      this.addBtn.disabled = false;
      this.addLabel.textContent = 'Add set to cart';
    }
  }
}

if (!customElements.get('set-builder')) {
  customElements.define('set-builder', SetBuilder);
}
